import { useEffect, useState } from "react";
import { api, today, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, CalendarDays } from "lucide-react";

const TIPO_COLOR = {
  evento: "bg-blue-50 text-blue-600",
  pago: "bg-emerald-50 text-emerald-600",
  vencimiento: "bg-red-50 text-red-600",
  recordatorio: "bg-amber-50 text-amber-600",
};

export default function Calendario() {
  const { can } = useAuth();
  const canEdit = can("calendario", "edit");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ titulo: "", fecha: today(), tipo: "evento", notas: "" });

  const load = () => api.get("/calendario").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/calendario", form);
      toast.success("Evento agregado");
      setForm({ titulo: "", fecha: today(), tipo: "evento", notas: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/calendario/${id}`); load(); };

  const input = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";
  const upcoming = [...rows].sort((a, b) => a.fecha.localeCompare(b.fecha));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {canEdit && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm h-fit">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-slate-900">Nuevo evento</h3>
          </div>
          <form onSubmit={submit} className="p-6 space-y-3">
            <input data-testid="cal-titulo" className={input} placeholder="Título" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} required />
            <input data-testid="cal-fecha" type="date" className={input} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} required />
            <select data-testid="cal-tipo" className={input} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
              <option value="evento">Evento</option>
              <option value="pago">Pago</option>
              <option value="vencimiento">Vencimiento</option>
              <option value="recordatorio">Recordatorio</option>
            </select>
            <textarea data-testid="cal-notas" className={input} rows={2} placeholder="Notas (opcional)" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            <button data-testid="cal-submit" className="w-full bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center justify-center gap-2"><Plus className="h-4 w-4" /> Agregar</button>
          </form>
        </div>
      )}

      <div className={`bg-white border border-slate-200 rounded-md shadow-sm ${canEdit ? "lg:col-span-2" : "lg:col-span-3"}`}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-slate-500" />
          <h3 className="font-heading font-semibold text-slate-900">Agenda</h3>
        </div>
        <div className="divide-y divide-slate-100" data-testid="calendario-list">
          {upcoming.length === 0 && <p className="px-6 py-10 text-center text-slate-400 text-sm">Sin eventos programados</p>}
          {upcoming.map((r) => (
            <div key={r.id} className="px-6 py-4 flex items-start gap-4 hover:bg-slate-50/50">
              <div className="text-center min-w-[52px]">
                <p className="font-heading text-lg font-bold text-slate-900 leading-none">{r.fecha.slice(8)}</p>
                <p className="text-xs text-slate-400 uppercase">{new Date(r.fecha + "T00:00:00").toLocaleString("es-MX", { month: "short" })}</p>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">{r.titulo}</p>
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${TIPO_COLOR[r.tipo] || "bg-slate-100 text-slate-600"}`}>{r.tipo}</span>
                </div>
                {r.notas && <p className="text-sm text-slate-500 mt-0.5">{r.notas}</p>}
              </div>
              {canEdit && <button data-testid={`cal-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
